import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  FormattedMessage,
  injectIntl,
} from 'react-intl'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import NavigationBar from '../../partials/NavigationBar'
import {
  DivAccount,
  AuthCard,
  Subtitle,
  Asterisk,
  Title,
  MessageError,
  TitleError,
  Message,
  IsReq,
  StyledInput,
  Label,
  Foot,
  ButtonWhite,
  PhoneInputStyled,
} from './styledComponents'

const Profile = ({ values, onNext, onPrevious, logout, onboarding = false, profile = null, intl: { formatMessage } }) => {
  const firstnameInput = useRef(null)
  const lastnameInput = useRef(null)
  const companyInput = useRef(null)
  const positionInput = useRef(null)
  const phoneInput = useRef(null)
  const secondaryPhoneInput = useRef(null)

  const [error, setError] = useState('')
  const [phoneValuePrimary, setPhoneValuePrimary] = useState('')
  const [phoneValueSecondary, setPhoneValueSecondary] = useState('')
  const [errorFields, setErrorFields] = useState([])

  const required = [
    { name: 'first_name', input: firstnameInput },
    { name: 'last_name', input: lastnameInput },
    { name: 'company', input: companyInput },
    { name: 'position', input: positionInput },
  ]

  useEffect(() => {
    const { profile } = values
    if (profile !== null) {
      required.forEach(i => {
        if (i.input.current) {
          i.input.current.value = profile[i.name]
        }
      })
      setPhoneValuePrimary(profile.main_phone)
      setPhoneValueSecondary(profile.secondary_phone)
    }
  }, [values])

  const getData = () => {
    const data = {}
    required.forEach(i => {
      if (i.input.current) {
        data[i.name] = i.input.current.value
      }
    })

    data.main_phone = phoneValuePrimary
    data.secondary_phone = phoneValueSecondary
    return data
  }

  const validate = () => {
    const newErrorFields = []
    const isRequired = required.find(({ name, input }) => {
      const required = !((input.current?.value || '').trim())
      required ? newErrorFields.push(name) : null
      return required
    })

    if (isRequired) {
      return (
        formatMessage({
          id: 'auth.profile.invalid.allInfo',
          defaultMessage: 'Please provide all the required information (*)',
          description: 'Please provide all the required information (*)',
        })
      )
    }

    let parsedNumber
    if (typeof phoneValuePrimary === 'string' && phoneValuePrimary.length > 1) {
      parsedNumber = parsePhoneNumberFromString(phoneValuePrimary)
    }

    if (
      parsedNumber === undefined ||
      !parsedNumber.isPossible()
    ) {
      return (
        formatMessage({
          id: 'auth.profile.invalid.phone',
          defaultMessage: 'Main phone # invalid',
          description: 'Main phone # invalid',
        })
      )
    }

    // Empty means no errors, so IT IS valid input
    return ''
  }

  const next = () => {
    const errorMessage = validate()
    if (errorMessage) {
      setError(errorMessage)
      window.scrollTo(0, 0)
    } else {
      const profile = getData()
      onNext({ profile })
    }
  }

  const hasError = f => errorFields.includes(f)

  return (
    <DivAccount>
      <NavigationBar
        logOutOnly
        logOut={logout}
        name={
          formatMessage({
            id: 'auth.address.title',
            defaultMessage: 'Profile Information',
            description: 'Profile Information',
          })
        }
      />
      <AuthCard>

        <Title>
          <FormattedMessage
            id='auth.profile.heading'
            defaultMessage='Account Information'
            description='Account Information label'
          />
        </Title>
        <Subtitle>
          <FormattedMessage
            id='auth.address.where'
            defaultMessage='Knowing more about you helps us improve your Altrac experience. Fields marked with a (*) are required.'
            description='Knowing where you are label'
          />
        </Subtitle>

        {
          error && (
            <MessageError>
              <TitleError>
                <FormattedMessage
                  id='auth.address.invalid.label'
                  defaultMessage='INVALID INPUT'
                  description='INVALID INPUT label'
                />
              </TitleError>
              <Message>
                {error}
              </Message>
            </MessageError>
          )
        }

        <Label>
          <FormattedMessage
            id='invite.firstName'
            defaultMessage='First Name'
            description='First Name label'
          />
          <IsReq>
            *
          </IsReq>
          <StyledInput
            ref={firstnameInput}
            error={hasError('first_name')}
            name='fname'
            autoComplete='given-name'
            required
            autoFocus
          />
        </Label>

        <Label>
          <FormattedMessage
            id='invite.lastName'
            defaultMessage='Last Name'
            description='Last Name label'
          />
          <IsReq>
            *
          </IsReq>
          <StyledInput
            ref={lastnameInput}
            error={hasError('last_name')}
            name='lname'
            autoComplete='family-name'
            required
          />
        </Label>

        <Label>
          <FormattedMessage
            id='auth.profile.company'
            defaultMessage='Company'
            description='Company label'
          />
          <IsReq>
            *
          </IsReq>
          <StyledInput
            ref={companyInput}
            error={hasError('company')}
          />
        </Label>

        <Label>
          <FormattedMessage
            id='auth.profile.position'
            defaultMessage='Position/Job Title'
            description='Position/Job Title label'
          />
          <IsReq>
            *
          </IsReq>
          <StyledInput
            ref={positionInput}
            error={hasError('position')}
          />
        </Label>
        <Foot>
          <FormattedMessage
            id='auth.profile.bestSupport'
            defaultMessage='This helps us provide the best support possible'
            description='This helps us provide the best support possible label'
          />
        </Foot>

        <Label>
          <FormattedMessage
            id='auth.profile.phonePrimary'
            defaultMessage='Main Phone #'
            description='Main Phone # label'
          />
          <IsReq>
            *
          </IsReq>
          <PhoneInputStyled
            value={phoneValuePrimary}
            onChange={(phoneValuePrimaryNew) => {
              setPhoneValuePrimary(phoneValuePrimaryNew)
            }}
            country='US'
            countries={['US', 'CA', 'CL']}
            displayInitialValueAsLocalNumber
            error={hasError('main_phone') ? 'Invalid phone number' : undefined}
            name='phone'
            autoComplete='tel'
            required
          />
        </Label>
        <Foot>
          <FormattedMessage
            id='auth.profile.phonePrimaryReason'
            defaultMessage='Enter the phone # you want to use for Altrac Alerts'
            description='Enter the phone # you want to use for Altrac Alerts label'
          />
        </Foot>

        <Label>
          <FormattedMessage
            id='auth.profile.phoneSecondary'
            defaultMessage='Secondary Phone #'
            description='Secondary Phone # label'
          />
          <PhoneInputStyled
            value={phoneValueSecondary}
            onChange={(phoneValueSecondaryNew) => {
              setPhoneValueSecondary(phoneValueSecondaryNew)
            }}
            country='US'
            countries={['US', 'CA', 'CL']}
            displayInitialValueAsLocalNumber
            name='phone'
            autoComplete='tel'
          />
        </Label>
        <Foot>
          <FormattedMessage
            id='auth.profile.phoneSecondaryReason'
            defaultMessage='A number we will use in case of emergency'
            description='A number we will use in case of emergency label'
          />
        </Foot>

        <ButtonWhite onClick={next}>
          <FormattedMessage
            id='resetPassword.next'
            defaultMessage='NEXT'
            description='NEXT label'
          />
        </ButtonWhite>

      </AuthCard>
    </DivAccount>
  )
}

Profile.propTypes = {
  profile: PropTypes.object,
  onboarding: PropTypes.bool,
  values: PropTypes.object,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  logout: PropTypes.func,
  intl: PropTypes.object,
}

export default injectIntl(Profile)
